import { Paper } from '@material-ui/core';
import TextHeader from 'components/CustomText/TextHeader';
import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import NumberListFormItem from 'components/Application/InitialApplication/NumberListFormItem';
import CustomTextField from 'components/Input/CustomTextField';
import CustomCheckbox from 'components/CustomCheckbox';
import { addRemoveRequest, deleteRemoveRequest, getReplacedValue, produce, isHighlight as isHL } from 'utility/general';
import CustomOutlineSelect from 'components/Input/CustomOutlineSelect';
import DataListener from './DataListener';
import { useDispatch, useSelector } from 'react-redux';
import { SET_SITE_VALIDATION, SET_STUDY_VALIDATION, UPDATE_FORM_VALIDITY } from 'store/key';
import { getStore } from 'store';
import { checkNull } from 'utility/formValidate';

const SPaperLayout = styled.div`
    padding: 10px;
`;

const SLayout = styled.div`
    padding: 10px;
`;

export default function StudyDetailsFormLayout(props) {
    const {
        status = {},
        isAmendment = {},
        tabType,
        defaultData = {},
        disableOnPermission = false,
        onFormUpdate = (key, value) => null,
        onSiteInfoUpdate = (siteInfoKey, key, value) => { },
        applicationDetail = {},
        siteInfoId,
        formId,
        allComments,
        getCommentDetail,
        isEnableComment,
    } = props;
    const [postData, setPostData] = useState({});
    const [sitePostData, setSitePostData] = useState({});

    const [studyInfoValidation, setStudyInfoValidation] = useState({});
    const [siteInfoValidation, setSiteInfoValidation] = useState({});

    const validationKey = 'studyDetails';

    const isStudyValid = useSelector(
        (state) => state.applicationDetailValid.isApplicationFormDetailValid?.[validationKey],
    );

    const isSiteValid = useSelector(
        (state) => state.applicationDetailValid.siteInfoValid?.[siteInfoId]?.[validationKey],
    );

    const dispatch = useDispatch();

    const getMinNumber = () => {
        const tempCreateDtm = applicationDetail?.applicationForm?.subNo;
        if (tempCreateDtm) {
            const dateInSubNo = tempCreateDtm?.toString().split('-')[0];
            const year = parseInt(dateInSubNo.substring(0, 4), 10);
            const month = parseInt(dateInSubNo.substring(4, 6), 10) - 1; // Months are 0-based in JavaScript
            const day = parseInt(dateInSubNo.substring(6, 8), 10);
            if (new Date(year, month, day) < new Date('2025-05-01')) {
                return 3;
            }
        }
        return 5;
    };

    // initialize the study-info postData if edit data exist
    useEffect(() => {
        if (tabType === 'info') {
            const { applicationDetailPostData } = getStore().getState();
            const postData = applicationDetailPostData.postData?.applicationForm ?? {};
            setPostData(postData);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // initialize the site postData if edit data exist
    useEffect(() => {
        if (tabType === 'hospital') {
            const { applicationDetailPostData } = getStore().getState();
            const siteInfo = applicationDetailPostData.postData.siteInfo;
            const currentSiteInfoPostData = siteInfo?.find((item) => item.id === siteInfoId);
            setSitePostData(currentSiteInfoPostData ? { [siteInfoId]: currentSiteInfoPostData } : {});
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // update study-info validity to the reducer
    useEffect(() => {
        const validations = Object.values(studyInfoValidation);

        // data is not initialized
        if (validations.length === 0) {
            return;
        }

        const currentStatus = validations.every((item) => item);

        if (currentStatus !== isStudyValid) {
            dispatch({
                type: UPDATE_FORM_VALIDITY,
                payload: {
                    type: 'study',
                    applicationFormId: formId,
                    formKey: validationKey,
                    isValid: currentStatus,
                },
            });
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [studyInfoValidation]);

    // update site-info validity to the reducer
    useEffect(() => {
        const validations = Object.values(siteInfoValidation);

        // data is not initialized
        if (validations.length === 0) {
            return;
        }

        const currentStatus = validations.every((item) => item);

        if (currentStatus !== isSiteValid) {
            dispatch({
                type: UPDATE_FORM_VALIDITY,
                payload: {
                    type: 'site',
                    applicationFormId: formId,
                    siteInfoId: siteInfoId,
                    formKey: validationKey,
                    isValid: currentStatus,
                },
            });
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [siteInfoValidation]);

    const updateSiteValidation = (name, isValidate) => {
        setSiteInfoValidation(
            produce((draft) => {
                draft[name] = isValidate;
            }),
        );
    };

    const updateStudyValidation = (name, isValidate) => {
        setStudyInfoValidation(
            produce((draft) => {
                draft[name] = isValidate;
            }),
        );
    };

    // when siteInfoValidation change, update the reducer
    useEffect(() => {
        dispatch({
            type: SET_SITE_VALIDATION,
            payload: {
                siteInfoId,
                [validationKey]: siteInfoValidation,
            },
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [siteInfoValidation]);

    // when studyInfoValidation change, update the reducer
    useEffect(() => {
        dispatch({
            type: SET_STUDY_VALIDATION,
            payload: {
                [validationKey]: studyInfoValidation,
            },
        });

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [studyInfoValidation]);

    const getData = (key) => {
        return postData[key] ?? defaultData[key];
    };

    const getDefaultData = (key) => {
        const site = applicationDetail.siteInfo.find((item) => {
            return item.id === siteInfoId;
        });

        return site?.[key];
    };

    const getSiteData = (key) => {
        return sitePostData?.[siteInfoId]?.[key] ?? getDefaultData(key);
    };

    const onFieldChange = (e) => {
        const { name, value } = e.target;

        setPostData((prevData) => {
            return {
                ...prevData,
                [name]: value,
            };
        });

        onFormUpdate(name, value);
    };

    const onSiteFieldChange = (e) => {
        const { value, name } = e.target;

        setSitePostData((prevData) => {
            const newData = produce(prevData, (draft) => {
                if (!draft[siteInfoId]) {
                    draft[siteInfoId] = {};
                }

                draft[siteInfoId][name] = value;
            });

            return newData;
        });

        onSiteInfoUpdate(siteInfoId, name, value);
    };

    const updateSiteProspectiveValidation = (isProspective) => {
        const listOfSite = applicationDetail.siteInfo.map((item) => item.id);
        const { applicationDetailValid, applicationDetailPostData } = getStore().getState();

        listOfSite.forEach((siteId) => {
            // do checking
            const currentSiteInfo = applicationDetail.siteInfo.find((item) => item.id === siteId);

            if (!isProspective) {
                applicationDetailValid.siteInfoValid[siteId].studyDetailsForProspectiveStudy = true;
            } else {
                // check if data is null
                const { p2NumOfExtra, p2OtherInvestigator, p2WhomWhere } = currentSiteInfo;
                const siteData = applicationDetailPostData.postData?.siteInfo?.find((item) => item.id === siteId);

                let isValid = true;

                if (
                    checkNull(getReplacedValue(p2NumOfExtra, siteData?.p2NumOfExtra)) ||
                    checkNull(getReplacedValue(p2OtherInvestigator, siteData?.p2OtherInvestigator))
                ) {
                    isValid = false;
                }

                if (getReplacedValue(p2OtherInvestigator, siteData?.p2OtherInvestigator) === true) {
                    if (checkNull(getReplacedValue(p2WhomWhere, siteData?.p2WhomWhere))) {
                        isValid = false;
                    }
                }

                applicationDetailValid.siteInfoValid[siteId].studyDetailsForProspectiveStudy = isValid;
            }
        });
    };

    const updateSiteProspectiveForm = (isProspective) => {
        // p2NumOfExtra: true, p2OtherInvestigator: true, p2WhomWhere: true

        const listOfSite = applicationDetail.siteInfo.map((item) => item.id);

        if (isProspective) {
            listOfSite.forEach((siteId) => {
                deleteRemoveRequest({
                    applicationFormId: formId,
                    keys: ['p2NumOfExtra', 'p2OtherInvestigator', 'p2WhomWhere'],
                    siteInfoId: siteId,
                });
            });
        } else {
            listOfSite.forEach((siteId) => {
                addRemoveRequest({
                    applicationFormId: formId,
                    name: ['p2NumOfExtra', 'p2OtherInvestigator', 'p2WhomWhere'],
                    siteInfoId: siteId,
                });
            });
        }
    };

    const onMultipleCheckboxChange = (name, value) => {
        const isAllSelect = value.every((item) => item.value);
        let prospectiveValue = '';

        if (isAllSelect) {
            // Option all is selected, "B" means "Both"
            setPostData(
                produce((draft) => {
                    draft[name] = 'B';
                }),
            );
            prospectiveValue = 'B';
            onFormUpdate(name, 'B');
        } else {
            setPostData(
                produce((draft) => {
                    value.forEach((item) => {
                        if (item.value) {
                            draft[name] = item.key;
                            prospectiveValue = item.key;
                            onFormUpdate(name, item.key);
                        }
                    });
                }),
            );
        }

        // update site data regarding to study-info 8.1
        updateSiteProspectiveForm(prospectiveValue === 'P' || prospectiveValue === 'B');

        // update site data validation rule regarding to site-info 8.1
        updateSiteProspectiveValidation(prospectiveValue === 'P' || prospectiveValue === 'B');
    };

    const getStudyDesignValue = () => {
        const currentValue = getData('p2StudyDesign');

        if (!currentValue) {
            return [];
        }

        if (currentValue === 'B') {
            return ['P', 'R'];
        } else {
            return [currentValue];
        }
    };

    const shouldDisableOnSiteInfo = () => {
        return tabType === 'info';
    };

    const isHighlight = (keys, istable = false) => {
        return isHL(applicationDetail, siteInfoId, formId, tabType, keys, istable);
    };

    const onStudyDesignChanged = (e) => {
        if (e.target.value !== 9) {
            setPostData((prevData) => {
                const newData = { ...prevData, p2Other: '' };
                return newData;
            });
            onFormUpdate('p2Other', '');
        }
    };

    return (
        <SPaperLayout>
            <DataListener
                onPostDataReset={() => {
                    setSitePostData({});
                    setPostData({});
                }}
            />
            <Paper>
                <SLayout>
                    <TextHeader underline italic>
                        PART IIA: STUDY DETAILS
                    </TextHeader>
                    {tabType === 'hospital' ? (
                        <>
                            <NumberListFormItem number={'7'} label={'Study subjects'} bold fontSize={16} />
                            <NumberListFormItem number={'7.4'} highlight={isHighlight(['p2NumSubject'])}
                                isEnableComment={isEnableComment}
                                commentProps={{
                                    commentLabel: '7.4 Number of subjects to be recruited locally in applying site(s)',
                                    commentField: 'p2NumSubject',
                                    commentDetail: getCommentDetail("p2NumSubject", allComments),
                                }}
                            >
                                <CustomTextField
                                    disableOnPermission={disableOnPermission}
                                    debounceChange
                                    key={`p2NumSubject${siteInfoId}`}
                                    value={getSiteData('p2NumSubject')}
                                    name={'p2NumSubject'}
                                    // style={{ width: 500 }}
                                    // numberField
                                    tooltip={'No. of subjects to be recruited in the site(s) stated in Q.2.3.1 above.'}
                                    onChange={onSiteFieldChange}
                                    onAppFieldValidate={(name, isValid) => {
                                        updateSiteValidation(name, isValid);
                                    }}
                                    maxLength={20}
                                    label={'Number of subjects to be recruited locally in applying site(s)'}
                                />
                            </NumberListFormItem>
                            <NumberListFormItem
                                number={'7.5'}
                                highlight={isHighlight(['p2SubjectRecruit'])}
                                alignItems="flex-start"
                                isEnableComment={isEnableComment}
                                commentProps={{
                                    commentLabel: '7.5 How will subject be identified and recruited',
                                    commentField: 'p2SubjectRecruit',
                                    commentDetail: getCommentDetail("p2SubjectRecruit", allComments),
                                }}
                            >
                                <CustomTextField
                                    disableOnPermission={disableOnPermission}
                                    key={`p2SubjectRecruit${siteInfoId}`}
                                    debounceChange
                                    value={getSiteData('p2SubjectRecruit')}
                                    name={'p2SubjectRecruit'}
                                    onChange={onSiteFieldChange}
                                    onAppFieldValidate={updateSiteValidation}
                                    maxLength={30000}
                                    isTextArea
                                    textAreaMinRow={12}
                                    label={'How will subject be identified and recruited'}
                                />
                            </NumberListFormItem>
                        </>
                    ) : (
                        <>
                            <NumberListFormItem number={'6'} label={'Scientific Basis'} bold fontSize={16} />
                            <NumberListFormItem
                                number={'6.1'}
                                alignItems={'flex-start'}
                                highlight={isHighlight(['p2Aim'])}
                                isEnableComment={isEnableComment}
                                commentProps={{
                                    commentLabel: '6.1 Aim of study',
                                    commentField: 'p2Aim',
                                    commentDetail: getCommentDetail("p2Aim", allComments),
                                }}
                            >
                                <CustomTextField
                                    disableOnPermission={disableOnPermission}
                                    debounceChange
                                    value={getData('p2Aim')}
                                    name={'p2Aim'}
                                    onChange={onFieldChange}
                                    onAppFieldValidate={updateStudyValidation}
                                    maxLength={30000}
                                    isTextArea
                                    textAreaMinRow={5}
                                    label={'Aim of study'}
                                />
                            </NumberListFormItem>
                            <NumberListFormItem
                                number={'6.2'}
                                highlight={isHighlight(['p2Hypothesis'])}
                                alignItems="flex-start"
                                isEnableComment={isEnableComment}
                                commentProps={{
                                    commentLabel: '6.2 Hypothesis',
                                    commentField: 'p2Hypothesis',
                                    commentDetail: getCommentDetail("p2Hypothesis", allComments),
                                }}
                            >
                                <CustomTextField
                                    disableOnPermission={disableOnPermission}
                                    debounceChange
                                    value={getData('p2Hypothesis')}
                                    name={'p2Hypothesis'}
                                    onAppFieldValidate={updateStudyValidation}
                                    onChange={onFieldChange}
                                    maxLength={30000}
                                    isTextArea
                                    textAreaMinRow={5}
                                    tooltip="E.g. Compared to x control, y intervention leads to a greater rate of z outcome"
                                    label={'Hypothesis'}
                                />
                            </NumberListFormItem>
                            <NumberListFormItem label={'Outcome measure(s)'} number={'6.3'} />
                            <NumberListFormItem
                                number={'6.3.1'}
                                level={2}
                                highlight={isHighlight(['p2PrimaryOutcome'])}
                                alignItems="flex-start"
                                isEnableComment={isEnableComment}
                                commentProps={{
                                    commentLabel: '6.3.1 Primary outcome(s)',
                                    commentField: 'p2PrimaryOutcome',
                                    commentDetail: getCommentDetail("p2PrimaryOutcome", allComments),
                                }}
                            >
                                <CustomTextField
                                    disableOnPermission={disableOnPermission}
                                    debounceChange
                                    value={getData('p2PrimaryOutcome')}
                                    name={'p2PrimaryOutcome'}
                                    onChange={onFieldChange}
                                    maxLength={30000}
                                    isTextArea
                                    textAreaMinRow={5}
                                    onAppFieldValidate={updateStudyValidation}
                                    label={'Primary outcome(s)'}
                                />
                            </NumberListFormItem>
                            <NumberListFormItem
                                number={'6.3.2'}
                                level={2}
                                highlight={isHighlight(['p2SecondaryOutcome'])}
                                alignItems="flex-start"
                                isEnableComment={isEnableComment}
                                commentProps={{
                                    commentLabel: '6.3.2 Secondary outcome(s)',
                                    commentField: 'p2SecondaryOutcome',
                                    commentDetail: getCommentDetail("p2SecondaryOutcome", allComments),
                                }}
                            >
                                <CustomTextField
                                    disableOnPermission={disableOnPermission}
                                    debounceChange
                                    label={'Secondary outcome(s)'}
                                    value={getData('p2SecondaryOutcome')}
                                    name={'p2SecondaryOutcome'}
                                    maxLength={30000}
                                    isTextArea
                                    textAreaMinRow={5}
                                    isValidValue={(isValid) => {
                                        updateStudyValidation('p2SecondaryOutcome', isValid);
                                    }}
                                    // onAppFieldValidate={updateStudyValidation} //optional field
                                    onChange={onFieldChange}
                                />
                            </NumberListFormItem>
                            <NumberListFormItem
                                number={'7'}
                                label={'Study Subjects'}
                                bold
                                fontSize={16}
                                style={{ marginTop: 30 }}
                            />
                            <NumberListFormItem
                                number={'7.1'}
                                highlight={isHighlight(['p2Inclusion'])}
                                alignItems="flex-start"
                                isEnableComment={isEnableComment}
                                commentProps={{
                                    commentLabel: '7.1 Inclusion criteria',
                                    commentField: 'p2Inclusion',
                                    commentDetail: getCommentDetail("p2Inclusion", allComments),
                                }}
                            >
                                <CustomTextField
                                    disableOnPermission={disableOnPermission}
                                    debounceChange
                                    label={'Inclusion criteria'}
                                    value={getData('p2Inclusion')}
                                    name={'p2Inclusion'}
                                    maxLength={30000}
                                    isTextArea
                                    textAreaMinRow={5}
                                    onChange={onFieldChange}
                                    onAppFieldValidate={updateStudyValidation}
                                />
                            </NumberListFormItem>
                            <NumberListFormItem
                                number={'7.2'}
                                highlight={isHighlight(['p2Exclusion'])}
                                alignItems="flex-start"
                                isEnableComment={isEnableComment}
                                commentProps={{
                                    commentLabel: '7.2 Exclusion criteria',
                                    commentField: 'p2Exclusion',
                                    commentDetail: getCommentDetail("p2Exclusion", allComments),
                                }}
                            >
                                <CustomTextField
                                    disableOnPermission={disableOnPermission}
                                    debounceChange
                                    label={'Exclusion criteria'}
                                    value={getData('p2Exclusion')}
                                    name={'p2Exclusion'}
                                    maxLength={30000}
                                    isTextArea
                                    textAreaMinRow={5}
                                    onChange={onFieldChange}
                                    onAppFieldValidate={updateStudyValidation}
                                />
                            </NumberListFormItem>
                            <NumberListFormItem
                                number={'7.3'}
                                label={'Sample-size of the whole study and rationale for calculation'}
                            />
                            <NumberListFormItem number={'7.3.1'} level={2} highlight={isHighlight(['p2Sample'])}
                                isEnableComment={isEnableComment}
                                commentProps={{
                                    commentLabel: '7.3.1 Sample size',
                                    commentField: 'p2Sample',
                                    commentDetail: getCommentDetail("p2Sample", allComments),
                                }}
                            >
                                <CustomTextField
                                    disableOnPermission={disableOnPermission}
                                    debounceChange
                                    value={getData('p2Sample')}
                                    name={'p2Sample'}
                                    onChange={onFieldChange}
                                    maxLength={600}
                                    label={'Sample size'}
                                    tooltip="Please state the sample size of the entire study. Specify the no. of subjects for each HA site and non-HA site. "
                                    onAppFieldValidate={updateStudyValidation}
                                />
                            </NumberListFormItem>
                            <NumberListFormItem
                                number={'7.3.2'}
                                level={2}
                                highlight={isHighlight(['p2Rationale'])}
                                alignItems="flex-start"
                                isEnableComment={isEnableComment}
                                commentProps={{
                                    commentLabel: '7.3.2 Based on the following rationale',
                                    commentField: 'p2Rationale',
                                    commentDetail: getCommentDetail("p2Rationale", allComments),
                                }}
                            >
                                <CustomTextField
                                    disableOnPermission={disableOnPermission}
                                    debounceChange
                                    label={'Based on the following rationale'}
                                    value={getData('p2Rationale')}
                                    name={'p2Rationale'}
                                    maxLength={30000}
                                    isTextArea
                                    textAreaMinRow={5}
                                    onChange={onFieldChange}
                                    tooltip={'“Drop-out rate” should be considered when calculating the sample size.'}
                                    onAppFieldValidate={updateStudyValidation}
                                />
                            </NumberListFormItem>
                            <NumberListFormItem
                                disableOnPermission={disableOnPermission}
                                number={'7.3.3'}
                                showTrueFalse
                                level={2}
                                label={'Will the study involve HA patients?'}
                                trueFalseValue={getData('p2HAPatients')}
                                name={'p2HAPatients'}
                                onTrueFalseSelected={onFieldChange}
                                onAppFieldValidate={updateStudyValidation}
                                highlight={isHighlight(['p2HAPatients'])}
                                isEnableComment={isEnableComment}
                                commentProps={{
                                    commentLabel: '7.3.3 Will the study involve HA patients?',
                                    commentField: 'p2HAPatients',
                                    commentDetail: getCommentDetail("p2HAPatients", allComments),
                                }}
                            />

                            <NumberListFormItem number={'7.3.3.1'} level={3} highlight={isHighlight(['p2NumPatients'])}>
                                <CustomTextField
                                    disableOnPermission={disableOnPermission}
                                    debounceChange
                                    value={getData('p2NumPatients')}
                                    name={'p2NumPatients'}
                                    onChange={onFieldChange}
                                    numberField
                                    style={{ width: '100%' }}
                                    allowAction={getData('p2HAPatients') === true}
                                    updateApplicationFormOnDisableChange={formId}
                                    onAppFieldValidate={updateStudyValidation}
                                    label={
                                        'If yes, estimate the number of patients planned to be involved in the study?'
                                    }
                                    isEnableComment={isEnableComment}
                                    commentProps={{
                                        commentLabel: '7.3.3.1 If yes, estimate the number of patients planned to be involved in the study?',
                                        commentField: 'p2NumPatients',
                                        commentDetail: getCommentDetail("p2NumPatients", allComments),
                                    }}
                                />
                            </NumberListFormItem>
                            <NumberListFormItem
                                number={'7.3.3.2'}
                                level={3}
                                highlight={isHighlight(['p2RoleHA'])}
                                alignItems="flex-start"
                                isEnableComment={isEnableComment}
                                commentProps={{
                                    commentLabel: '7.3.3.2 If no, specify the role of HA in the study',
                                    commentField: 'p2RoleHA',
                                    commentDetail: getCommentDetail("p2RoleHA", allComments),
                                }}
                            >
                                <CustomTextField
                                    disableOnPermission={disableOnPermission}
                                    debounceChange
                                    value={getData('p2RoleHA')}
                                    name={'p2RoleHA'}
                                    maxLength={500}
                                    isTextArea
                                    textAreaMinRow={5}
                                    onChange={onFieldChange}
                                    updateApplicationFormOnDisableChange={formId}
                                    allowAction={getData('p2HAPatients') === false}
                                    label={'If no, specify the role of HA in the study'}
                                    onAppFieldValidate={updateStudyValidation}
                                />
                            </NumberListFormItem>
                            <NumberListFormItem
                                number={'7.4'}
                                showDisableMessage
                                displayContentNextLine
                                label={'Number of subjects to be recruited locally in applying site(s)'}
                                highlight={isHighlight(['p2NumSubject'])}
                                isEnableComment={isEnableComment}
                                commentProps={{
                                    commentLabel: '7.4 Number of subjects to be recruited locally in applying site(s)',
                                    commentField: 'p2NumSubject',
                                    commentDetail: getCommentDetail("p2NumSubject", allComments),
                                }}
                            >
                                <CustomTextField
                                    // style={{ width: 800 }}
                                    value={getData('p2NumSubject')}
                                    name={'p2NumSubject'}
                                    onChange={onFieldChange}
                                    disableOnPermission
                                />
                            </NumberListFormItem>
                            <NumberListFormItem
                                number={'7.5'}
                                label={'How will subject be identified and recruited'}
                                displayContentNextLine
                                showDisableMessage={shouldDisableOnSiteInfo()}
                                highlight={isHighlight(['p2SubjectRecruit'])}
                                alignItems="flex-start"
                                isEnableComment={isEnableComment}
                                commentProps={{
                                    commentLabel: '7.5 How will subject be identified and recruited',
                                    commentField: 'p2SubjectRecruit',
                                    commentDetail: getCommentDetail("p2SubjectRecruit", allComments),
                                }}
                            >
                                <CustomTextField
                                    style={{ width: 500 }}
                                    value={getData('p2SubjectRecruit')}
                                    name={'p2SubjectRecruit'}
                                    onChange={onFieldChange}
                                    maxLength={30000}
                                    isTextArea
                                    textAreaMinRow={5}
                                    disableOnPermission
                                />
                            </NumberListFormItem>
                            <NumberListFormItem
                                number={'8'}
                                label={'Study Design and Methodology'}
                                bold
                                style={{ marginTop: 30 }}
                                fontSize={16}
                            />
                            <NumberListFormItem
                                number={'8.1'}
                                label={'Study design'}
                                highlight={isHighlight(['p2StudyDesign'])}
                                labelWidth={145}
                                isEnableComment={isEnableComment}
                                commentProps={{
                                    commentLabel: '8.1 Study design',
                                    commentField: 'p2StudyDesign',
                                    commentDetail: getCommentDetail("p2StudyDesign", allComments),
                                }}
                            >
                                <CustomCheckbox
                                    disableOnPermission={disableOnPermission}
                                    onChange={onMultipleCheckboxChange}
                                    onAppFieldValidate={updateStudyValidation}
                                    name={'p2StudyDesign'}
                                    defaultData={[
                                        {
                                            key: 'P',
                                            value: getStudyDesignValue().includes('P'),
                                            display: 'Prospective',
                                        },
                                        {
                                            key: 'R',
                                            value: getStudyDesignValue().includes('R'),
                                            display: 'Retrospective',
                                        },
                                    ]}
                                    value={getStudyDesignValue()}
                                />
                            </NumberListFormItem>
                            <NumberListFormItem
                                number={'8.2'}
                                label={'Study design (Nature)'}
                                highlight={isHighlight(['p2StudyNature'])}
                                isEnableComment={isEnableComment}
                                commentProps={{
                                    commentLabel: '8.2 Study design (Nature)',
                                    commentField: 'p2StudyNature',
                                    commentDetail: getCommentDetail("p2StudyNature", allComments),
                                }}
                            >
                                <div style={{ marginLeft: 15 }}>
                                    <CustomOutlineSelect
                                        disableOnPermission={disableOnPermission}
                                        style={{ width: 350 }}
                                        name={'p2StudyNature'}
                                        value={getData('p2StudyNature')}
                                        onAppFieldValidate={updateStudyValidation}
                                        onChange={(e) => {
                                            onFieldChange(e);
                                            onStudyDesignChanged(e);
                                        }}
                                        choices={[
                                            { key: 0, display: 'Case report/series' },
                                            { key: 1, display: 'Case-control study' },
                                            { key: 2, display: 'Cohort study (Compare different cohorts)' },
                                            { key: 3, display: 'Cross-sectional study' },
                                            { key: 4, display: 'Diagnostic test (correlation) study' },
                                            { key: 5, display: 'Non-randomized controlled trial' },
                                            { key: 6, display: 'Qualitative study' },
                                            { key: 7, display: 'Questionnaire survey' },
                                            { key: 8, display: 'Randomized controlled trial' },
                                            { key: 10, display: 'Registry' },
                                            { key: 9, display: 'Others' },
                                        ]}
                                    />
                                </div>
                            </NumberListFormItem>
                            <NumberListFormItem number={'8.2.1'} level={2} highlight={isHighlight(['p2Other'])}
                                isEnableComment={isEnableComment}
                                commentProps={{
                                    commentLabel: '8.2.1 If Other, specify',
                                    commentField: 'p2Other',
                                    commentDetail: getCommentDetail("p2Other", allComments),
                                }}
                            >
                                <CustomTextField
                                    disableOnPermission={disableOnPermission}
                                    value={getData('p2Other')}
                                    debounceChange
                                    name={'p2Other'}
                                    allowAction={getData('p2StudyNature') === 9}
                                    onAppFieldValidate={updateStudyValidation}
                                    updateApplicationFormOnDisableChange={formId}
                                    onChange={onFieldChange}
                                    maxLength={255}
                                    label={'If Other, specify'}
                                />
                            </NumberListFormItem>
                            <NumberListFormItem
                                number={'8.3'}
                                label={'Control'}
                                highlight={isHighlight(['p2Control'])}
                                labelWidth={133}
                                isEnableComment={isEnableComment}
                                commentProps={{
                                    commentLabel: '8.3 Control',
                                    commentField: 'p2Control',
                                    commentDetail: getCommentDetail("p2Control", allComments),
                                }}
                            >
                                <div style={{ marginLeft: 15 }}>
                                    <CustomOutlineSelect
                                        style={{ width: 300 }}
                                        disableOnPermission={disableOnPermission}
                                        name={'p2Control'}
                                        value={getData('p2Control')}
                                        onAppFieldValidate={updateStudyValidation}
                                        onChange={onFieldChange}
                                        choices={[
                                            { key: 0, display: 'Placebo' },
                                            { key: 1, display: 'Active (alternative intervention)' },
                                            { key: 2, display: 'Dose comparison (same intervention)' },
                                            { key: 3, display: 'Concurrent control (cohort)' },
                                            { key: 4, display: 'Historical control (cohort)' },
                                            { key: 5, display: 'No control' },
                                            { key: 6, display: 'N/A' },
                                        ]}
                                    />
                                </div>
                            </NumberListFormItem>
                            <NumberListFormItem
                                number={'8.4'}
                                label={'Group assignment'}
                                highlight={isHighlight(['p2GroupAssignment'])}
                                labelWidth={133}
                                isEnableComment={isEnableComment}
                                commentProps={{
                                    commentLabel: '8.4 Group assignment',
                                    commentField: 'p2GroupAssignment',
                                    commentDetail: getCommentDetail("p2GroupAssignment", allComments),
                                }}
                            >
                                <div style={{ marginLeft: 15 }}>
                                    <CustomOutlineSelect
                                        disableOnPermission={disableOnPermission}
                                        style={{ width: 200 }}
                                        name={'p2GroupAssignment'}
                                        value={getData('p2GroupAssignment')}
                                        onAppFieldValidate={updateStudyValidation}
                                        onChange={onFieldChange}
                                        choices={[
                                            { key: 'P', display: 'Parallel' },
                                            { key: 'C', display: 'Crossover' },
                                            { key: 'F', display: 'Factorial' },
                                            { key: 'N', display: 'N/A' },
                                            { key: 'O', display: 'Others' },
                                        ]}
                                    />
                                </div>
                            </NumberListFormItem>
                            <NumberListFormItem
                                number={'8.5'}
                                label={'Disease group (choose the most appropriate one)'}
                                highlight={isHighlight(['p2DiseaseGroup'])}
                                isEnableComment={isEnableComment}
                                commentProps={{
                                    commentLabel: '8.5 Disease group (choose the most appropriate one)',
                                    commentField: 'p2DiseaseGroup',
                                    commentDetail: getCommentDetail("p2DiseaseGroup", allComments),
                                }}
                            >
                                <div style={{ marginLeft: 15 }}>
                                    <CustomOutlineSelect
                                        disableOnPermission={disableOnPermission}
                                        style={{ width: 300 }}
                                        name={'p2DiseaseGroup'}
                                        value={getData('p2DiseaseGroup')}
                                        onAppFieldValidate={updateStudyValidation}
                                        onChange={onFieldChange}
                                        choices={[
                                            { key: 0, display: 'Blood and blood-forming organs' },
                                            { key: 1, display: 'Circulatory system' },
                                            { key: 2, display: 'Conditions originating in the perinatal period' },
                                            { key: 3, display: 'Congenital malformation, chromosomal abnormality' },
                                            { key: 4, display: 'Digestive system' },
                                            { key: 5, display: 'Endocrine system' },
                                            { key: 6, display: 'ENT system' },
                                            { key: 7, display: 'Eye and adnexa' },
                                            { key: 8, display: 'Healthy volunteer' },
                                            { key: 9, display: 'Genitourinary system and sex organs' },
                                            { key: 10, display: 'Immune system' },
                                            { key: 11, display: 'Infection' },
                                            { key: 12, display: 'Injury, poisoning and other external causes' },
                                            { key: 13, display: 'Mental and behavioural disorders' },
                                            { key: 14, display: 'Mouth and tooth' },
                                            { key: 15, display: 'Musculoskeletal system and connective tissue' },
                                            { key: 16, display: 'Neoplasm' },
                                            { key: 17, display: 'Nervous system' },
                                            { key: 18, display: 'Nutritional and metabolic process' },
                                            { key: 19, display: 'Pregnancy, childbirth and the puerperium' },
                                            { key: 20, display: 'Public health and health services research' },
                                            { key: 21, display: 'Respiratory system' },
                                            { key: 22, display: 'Skin and subcutaneous tissue' },
                                            { key: 23, display: 'Symptoms and general pathology, NOS' },
                                            { key: 24, display: 'Others' },
                                            { key: 25, display: 'N/A' },
                                        ]}
                                    />
                                </div>
                            </NumberListFormItem>
                            <NumberListFormItem number={'8.5.1'} level={2} highlight={isHighlight(['p2KeyConditions'])}
                                isEnableComment={isEnableComment}
                                commentProps={{
                                    commentLabel: '8.5.1 Key conditions under study',
                                    commentField: 'p2KeyConditions',
                                    commentDetail: getCommentDetail("p2KeyConditions", allComments),
                                }}
                            >
                                <CustomTextField
                                    debounceChange
                                    disableOnPermission={disableOnPermission}
                                    value={getData('p2KeyConditions')}
                                    name={'p2KeyConditions'}
                                    maxLength={255}
                                    onChange={onFieldChange}
                                    onAppFieldValidate={updateStudyValidation}
                                    label={'Key conditions under study'}
                                    tooltip="E.g. Asthma; DM; etc."
                                />
                            </NumberListFormItem>
                            <NumberListFormItem
                                number={'9'}
                                style={{ marginTop: 30 }}
                                displayContentNextLine
                                bold
                                fontSize={16}
                                label={'Methods of Data Analysis'}
                                highlight={isHighlight(['p2MethodsOfAnalysis'])}
                                alignItems="flex-start"
                                isEnableComment={isEnableComment}
                                commentProps={{
                                    commentLabel: '9 Methods of Data Analysis',
                                    commentField: 'p2MethodsOfAnalysis',
                                    commentDetail: getCommentDetail("p2MethodsOfAnalysis", allComments),
                                }}
                            >
                                <CustomTextField
                                    debounceChange
                                    disableOnPermission={disableOnPermission}
                                    value={getData('p2MethodsOfAnalysis')}
                                    name={'p2MethodsOfAnalysis'}
                                    maxLength={30000}
                                    isTextArea
                                    textAreaMinRow={5}
                                    onAppFieldValidate={updateStudyValidation}
                                    onChange={onFieldChange}
                                />
                            </NumberListFormItem>
                            <NumberListFormItem
                                number={'10'}
                                bold
                                label={'Handling and Storage of Personal Data and Study Data'}
                                fontSize={16}
                                style={{ marginTop: 30 }}
                            />
                            <NumberListFormItem
                                number={'10.1'}
                                label={'Will personal data be stored during and after the study?'}
                                disableOnPermission={disableOnPermission}
                                showTrueFalse
                                onAppFieldValidate={updateStudyValidation}
                                trueFalseValue={getData('p2PersonalStore')}
                                name={'p2PersonalStore'}
                                onTrueFalseSelected={onFieldChange}
                                highlight={isHighlight(['p2PersonalStore'])}
                                isEnableComment={isEnableComment}
                                commentProps={{
                                    commentLabel: '10.1 Will personal data be stored during and after the study?',
                                    commentField: 'p2PersonalStore',
                                    commentDetail: getCommentDetail("p2PersonalStore", allComments),
                                }}
                            />
                            <NumberListFormItem
                                number={'10.2'}
                                highlight={isHighlight(['p2PersonalHandle'])}
                                alignItems="flex-start"
                                isEnableComment={isEnableComment}
                                commentProps={{
                                    commentLabel: '10.2 How will the personal and study data be handled and stored during and after the study?',
                                    commentField: 'p2PersonalHandle',
                                    commentDetail: getCommentDetail("p2PersonalHandle", allComments),
                                }}
                            >
                                <CustomTextField
                                    debounceChange
                                    disableOnPermission={disableOnPermission}
                                    value={getData('p2PersonalHandle')}
                                    name={'p2PersonalHandle'}
                                    maxLength={2000}
                                    isTextArea
                                    textAreaMinRow={5}
                                    onAppFieldValidate={updateStudyValidation}
                                    onChange={onFieldChange}
                                    label={
                                        'How will the personal and study data be handled and stored during and after the study?'
                                    }
                                />
                            </NumberListFormItem>
                            <NumberListFormItem
                                number={'10.3'}
                                highlight={isHighlight(['p2PersonalAccess'])}
                                alignItems="flex-start"
                                isEnableComment={isEnableComment}
                                commentProps={{
                                    commentLabel: '10.3 Who will have access to the personal and study data during and after the study?',
                                    commentField: 'p2PersonalAccess',
                                    commentDetail: getCommentDetail("p2PersonalAccess", allComments),
                                }}
                            >
                                <CustomTextField
                                    debounceChange
                                    value={getData('p2PersonalAccess')}
                                    disableOnPermission={disableOnPermission}
                                    name={'p2PersonalAccess'}
                                    maxLength={2000}
                                    isTextArea
                                    textAreaMinRow={5}
                                    onAppFieldValidate={updateStudyValidation}
                                    onChange={onFieldChange}
                                    label={
                                        'Who will have access to the personal and study data during and after the study?'
                                    }
                                />
                            </NumberListFormItem>
                            <NumberListFormItem
                                number={'10.4'}
                                highlight={isHighlight(['p2PersonalResponse'])}
                                alignItems="flex-start"
                                isEnableComment={isEnableComment}
                                commentProps={{
                                    commentLabel: '10.4 Who will be responsible for safekeeping of the personal and study data during and after the study?',
                                    commentField: 'p2PersonalResponse',
                                    commentDetail: getCommentDetail("p2PersonalResponse", allComments),
                                }}
                            >
                                <CustomTextField
                                    debounceChange
                                    value={getData('p2PersonalResponse')}
                                    disableOnPermission={disableOnPermission}
                                    name={'p2PersonalResponse'}
                                    maxLength={2000}
                                    isTextArea
                                    textAreaMinRow={5}
                                    onAppFieldValidate={updateStudyValidation}
                                    onChange={onFieldChange}
                                    label={
                                        'Who will be responsible for safekeeping of the personal and study data during and after the study?'
                                    }
                                />
                            </NumberListFormItem>
                            <NumberListFormItem
                                number={'10.5'}
                                highlight={isHighlight(['p2DataKeep'])}
                                alignItems="flex-start"
                                isEnableComment={isEnableComment}
                                commentProps={{
                                    commentLabel: '10.5 How many years will the personal and study data be kept after the study?',
                                    commentField: 'p2DataKeep',
                                    commentDetail: getCommentDetail("p2DataKeep", allComments),
                                }}
                            >
                                <CustomTextField
                                    debounceChange
                                    tooltip={`≥${getMinNumber()} years is required`}
                                    value={getData('p2DataKeep')}
                                    disableOnPermission={disableOnPermission}
                                    name={'p2DataKeep'}
                                    minNumber={getMinNumber()}
                                    numberField
                                    style={{ width: 500 }}
                                    onAppFieldValidate={updateStudyValidation}
                                    onChange={onFieldChange}
                                    label={'How many years will the personal and study data be kept after the study?'}
                                />
                            </NumberListFormItem>
                            <NumberListFormItem
                                number={'10.6'}
                                highlight={isHighlight(['p2DataAforesaidPeriod'])}
                                alignItems="flex-start"
                                isEnableComment={isEnableComment}
                                commentProps={{
                                    commentLabel: '10.6 Plan arrangement for the personal and study data after completion of the aforesaid storage period?',
                                    commentField: 'p2DataAforesaidPeriod',
                                    commentDetail: getCommentDetail("p2DataAforesaidPeriod", allComments),
                                }}
                            >
                                <CustomTextField
                                    debounceChange
                                    disableOnPermission={disableOnPermission}
                                    value={getData('p2DataAforesaidPeriod')}
                                    name={'p2DataAforesaidPeriod'}
                                    maxLength={2000}
                                    isTextArea
                                    textAreaMinRow={5}
                                    onAppFieldValidate={updateStudyValidation}
                                    onChange={onFieldChange}
                                    label={
                                        'Plan arrangement for the personal and study data after completion of the aforesaid storage period?'
                                    }
                                />
                            </NumberListFormItem>
                        </>
                    )}
                </SLayout>
            </Paper>
        </SPaperLayout>
    );
}
